// src/features/text/find-replace/FindAndReplace.tsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, TextField, Paper, Grid, FormGroup, FormControlLabel, Switch, ButtonGroup, Button, Tooltip, Alert, IconButton
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StyledFAQ from '../../common/components/StyledFAQ';
import { findMatches, FindOptions, Match } from '../utils/textUtils';

const FindAndReplace: React.FC = () => {
    const { t } = useTranslation('tools');
    const [inputText, setInputText] = useState<string>('');
    const [findTerm, setFindTerm] = useState<string>('');
    const [replaceTerm, setReplaceTerm] = useState<string>('');
    const [options, setOptions] = useState<FindOptions>({
        matchCase: false,
        wholeWord: false,
        isRegex: false,
    });
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1);
    const [error, setError] = useState<string>('');

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const matches = useMemo((): Match[] => {
        try {
            const results = findMatches(inputText, findTerm, options);
            setError(''); // Clear previous errors
            return results;
        } catch (e: any) {
            setError(t('text_find_replace_error_regex'));
            return [];
        }
    }, [inputText, findTerm, options, t]);

    // Reset current match index when text or find term changes
    useEffect(() => {
        setCurrentMatchIndex(-1);
    }, [inputText, findTerm, options]);

    // Effect to select text in the textarea
    useEffect(() => {
        if (currentMatchIndex > -1 && matches[currentMatchIndex] && inputRef.current) {
            const match = matches[currentMatchIndex];
            inputRef.current.focus();
            inputRef.current.setSelectionRange(match.startIndex, match.endIndex);

            // Scroll to the selection
            const lineHeight = 20; // Approximate line height
            const jump = Math.floor(match.startIndex / 80); // Approximate chars per line
            inputRef.current.scrollTop = jump * lineHeight;

        }
    }, [currentMatchIndex, matches]);

    const handleOptionChange = (key: keyof FindOptions, value: boolean) => {
        setOptions(prev => ({ ...prev, [key]: value }));
    };

    const handleFindNext = () => {
        if (matches.length === 0) return;
        setCurrentMatchIndex(prev => (prev + 1) % matches.length);
    };

    const handleReplace = () => {
        if (currentMatchIndex < 0 || matches.length === 0) return;

        const match = matches[currentMatchIndex];
        const newText =
            inputText.substring(0, match.startIndex) +
            replaceTerm +
            inputText.substring(match.endIndex);

        setInputText(newText);
        // The matches will be recalculated, and the next logical match will be found
        // The useEffect will reset the index, but we want to find the next item after replacement
        // A more advanced implementation might try to keep a "smart" index
    };

    const handleReplaceAll = () => {
        if (matches.length === 0) return;
        try {
            const flags = options.matchCase ? 'g' : 'gi';
            const pattern = options.isRegex ? findTerm : findTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const finalPattern = options.wholeWord ? `\\b${pattern}\\b` : pattern;
            const regex = new RegExp(finalPattern, flags);
            setInputText(inputText.replace(regex, replaceTerm));
        } catch (e) {
            setError(t('text_find_replace_error_regex'));
        }
    };

    const faqs = [
        { q: 'text_find_replace_faq1_q', a: 'text_find_replace_faq1_a' },
        { q: 'text_find_replace_faq2_q', a: 'text_find_replace_faq2_a' },
        { q: 'text_find_replace_faq3_q', a: 'text_find_replace_faq3_a' },
    ];

    return (
        <Box>
            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Main Text Area - Top Section */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" component="h3" gutterBottom>
                                {t('text_common_input_label')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={t('text_common_copy_output')}>
                                    <span>
                                        <IconButton onClick={() => navigator.clipboard.writeText(inputText)} size="small" disabled={!inputText}>
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('text_common_clear_input')}>
                                    <span>
                                        <IconButton onClick={() => setInputText('')} size="small" disabled={!inputText}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                        <TextField
                            multiline
                            rows={12}
                            fullWidth
                            variant="outlined"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder={t('text_common_input_placeholder')}
                            inputRef={inputRef}
                            sx={{ 
                                '& .MuiOutlinedInput-root': { 
                                    backgroundColor: 'action.hover',
                                    minHeight: '300px'
                                } 
                            }}
                        />
                    </Box>

                    {/* Search and Replace Controls - Bottom Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 2,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider'
                    }}>
                        {/* Find and Replace Inputs */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2
                        }}>
                            <TextField
                                fullWidth
                                label={t('text_find_replace_find_label')}
                                value={findTerm}
                                onChange={(e) => setFindTerm(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                            <TextField
                                fullWidth
                                label={t('text_find_replace_replace_label')}
                                value={replaceTerm}
                                onChange={(e) => setReplaceTerm(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        {/* Options */}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <FormGroup row sx={{ gap: 2, flexWrap: 'wrap' }}>
                                <FormControlLabel 
                                    control={<Switch checked={options.matchCase} onChange={(e) => handleOptionChange('matchCase', e.target.checked)} />} 
                                    label={t('text_find_replace_option_case')} 
                                />
                                <FormControlLabel 
                                    control={<Switch checked={options.wholeWord} onChange={(e) => handleOptionChange('wholeWord', e.target.checked)} />} 
                                    label={t('text_find_replace_option_word')} 
                                />
                                <Tooltip title={t('text_find_replace_option_regex_tooltip')}>
                                    <FormControlLabel 
                                        control={<Switch checked={options.isRegex} onChange={(e) => handleOptionChange('isRegex', e.target.checked)} />} 
                                        label={t('text_find_replace_option_regex')} 
                                    />
                                </Tooltip>
                            </FormGroup>
                        </Box>

                        {/* Actions and Status */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: 2, 
                            flexWrap: 'wrap' 
                        }}>
                            <ButtonGroup variant="contained" aria-label="find and replace actions">
                                <Button onClick={handleFindNext} disabled={matches.length === 0}>
                                    {t('text_find_replace_action_find')}
                                </Button>
                                <Button onClick={handleReplace} disabled={currentMatchIndex < 0}>
                                    {t('text_find_replace_action_replace')}
                                </Button>
                                <Button onClick={handleReplaceAll} disabled={matches.length === 0}>
                                    {t('text_find_replace_action_replace_all')}
                                </Button>
                            </ButtonGroup>
                            {findTerm && !error && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('text_find_replace_matches_found', {
                                        current: currentMatchIndex + 1,
                                        total: matches.length
                                    })}
                                </Typography>
                            )}
                        </Box>

                        {error && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Alert severity="error">{error}</Alert>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ my: 8 }}>
                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('text_find_replace_about_title')}
                </Typography>
                <Typography paragraph color="text.secondary" textAlign="center" maxWidth="md" mx="auto">
                    {t('text_find_replace_about_desc')}
                </Typography>
                <StyledFAQ
                    title={t('faq_title')}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export default FindAndReplace;